/**
 * Summary computation for a finished workout session.
 *
 * Aggregates the saved logs (today's session vs prior history) and detects
 * personal records, returning a single immutable snapshot ready for the
 * summary screen to render.
 */

function isFilled(s) {
  // A set is considered "executed" when reps > 0. Weight can legitimately be
  // 0 (e.g. bodyweight exercises such as pull-ups, dips, push-ups).
  return s != null && Number(s.reps) > 0;
}

function tonnageOf(sets) {
  return (sets || []).reduce((sum, s) => {
    if (!isFilled(s)) return sum;
    return sum + Number(s.weight_kg) * Number(s.reps);
  }, 0);
}

function bestSet(sets) {
  let best = null;
  (sets || []).forEach((s) => {
    if (!isFilled(s)) return;
    if (!best
      || Number(s.weight_kg) > Number(best.weight_kg)
      || (Number(s.weight_kg) === Number(best.weight_kg) && Number(s.reps) > Number(best.reps))) {
      best = s;
    }
  });
  return best;
}

function maxWeight(sets) {
  let m = 0;
  (sets || []).forEach((s) => {
    if (isFilled(s) && Number(s.weight_kg) > m) m = Number(s.weight_kg);
  });
  return m;
}

function countFilled(sets) {
  return (sets || []).filter(isFilled).length;
}

export function buildSummary({ workout, logs, todayIso, startTs, endTs }) {
  const today = todayIso || new Date().toISOString().split("T")[0];
  const exerciseDefs = workout?.exercises || [];

  // Today's logs for this workout — keyed by exercise name (case-insensitive).
  const todayLogsForWorkout = logs.filter(
    (l) => l.workout_id === workout.id && l.date === today
  );
  const todayByEx = new Map();
  todayLogsForWorkout.forEach((l) => {
    todayByEx.set((l.exercise_name || "").trim().toLowerCase(), l);
  });

  // Previous session for this workout = most recent date < today with any log.
  const priorDates = logs
    .filter((l) => l.workout_id === workout.id && l.date < today)
    .map((l) => l.date)
    .sort();
  const prevSessionDate = priorDates.length ? priorDates[priorDates.length - 1] : null;
  const prevLogsForWorkout = prevSessionDate
    ? logs.filter((l) => l.workout_id === workout.id && l.date === prevSessionDate)
    : [];

  let totalTonnage = 0;
  let previousTonnage = 0;
  const exerciseRows = [];
  const records = [];

  exerciseDefs.forEach((ex) => {
    const key = (ex.name || "").trim().toLowerCase();
    const todayLog = todayByEx.get(key);
    const todaySets = todayLog?.sets || [];
    const exTonnage = tonnageOf(todaySets);
    totalTonnage += exTonnage;
    const filledSets = countFilled(todaySets);
    if (filledSets === 0) return; // skipped exercises do not show in the detail

    // Historical max (across ALL workouts, by exercise name) excluding today.
    let historicalMaxWeight = 0;
    let historicalBestSet = null;
    logs.forEach((l) => {
      if ((l.exercise_name || "").trim().toLowerCase() !== key) return;
      if (l.date === today && l.workout_id === workout.id) return; // exclude this session
      (l.sets || []).forEach((s) => {
        if (!isFilled(s)) return;
        const w = Number(s.weight_kg);
        if (w > historicalMaxWeight) {
          historicalMaxWeight = w;
          historicalBestSet = s;
        } else if (historicalBestSet && w === historicalMaxWeight && Number(s.reps) > Number(historicalBestSet.reps)) {
          historicalBestSet = s;
        }
      });
    });
    const todayMax = maxWeight(todaySets);
    const isPR = todayMax > 0 && todayMax > historicalMaxWeight;
    const todayBest = bestSet(todaySets);

    if (isPR && todayBest) {
      records.push({
        name: ex.name,
        now: { weight_kg: Number(todayBest.weight_kg), reps: Number(todayBest.reps) },
        previous: historicalBestSet
          ? { weight_kg: Number(historicalBestSet.weight_kg), reps: Number(historicalBestSet.reps) }
          : null,
      });
    }

    exerciseRows.push({
      name: ex.name,
      sets: filledSets,
      // Represent reps as a concise range or fixed if all identical.
      repsLabel: (() => {
        const rs = todaySets.filter(isFilled).map((s) => Number(s.reps));
        if (rs.length === 0) return "";
        const min = Math.min(...rs);
        const max = Math.max(...rs);
        return min === max ? `${min}` : `${min}-${max}`;
      })(),
      tonnage: Math.round(exTonnage),
      isPR,
    });
  });

  prevLogsForWorkout.forEach((l) => {
    previousTonnage += tonnageOf(l.sets);
  });

  const start = startTs ? new Date(startTs) : null;
  const end = endTs ? new Date(endTs) : new Date();
  const durationMinutes = start
    ? Math.max(1, Math.round((end - start) / 60000))
    : null;

  // Previous session duration (if we ever stored it in log payload). We don't
  // persist it server-side yet, so we compare against null gracefully.
  const previousDurationMinutes = null;

  return {
    totalTonnage: Math.round(totalTonnage),
    previousTonnage: Math.round(previousTonnage),
    deltaTonnage: Math.round(totalTonnage - previousTonnage),
    exerciseCount: exerciseRows.length,
    exerciseRows,
    records,
    durationMinutes,
    previousDurationMinutes,
    previousSessionDate: prevSessionDate,
    startTs: start ? start.toISOString() : null,
    endTs: end.toISOString(),
  };
}

/* ---------- localStorage helpers (persistent start-timestamp) ----------- */

const STORAGE_PREFIX = "fitpro-workout-start-";

export function markWorkoutStart(workoutId) {
  if (!workoutId) return;
  try {
    // Preserve an existing start timestamp from the last 12 hours — this
    // covers the "close app + reopen + tap Inizia again" resume case, while
    // still treating an old stale value (e.g. abandoned session > 12h ago)
    // as a fresh start.
    const existing = window.localStorage.getItem(`${STORAGE_PREFIX}${workoutId}`);
    if (existing) {
      const age = Date.now() - new Date(existing).getTime();
      if (Number.isFinite(age) && age < 12 * 3600 * 1000) return;
    }
    window.localStorage.setItem(`${STORAGE_PREFIX}${workoutId}`, new Date().toISOString());
  } catch (err) {
    console.debug("[workoutSummary] markStart failed", err);
  }
}

export function readWorkoutStart(workoutId) {
  if (!workoutId) return null;
  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${workoutId}`) || null;
  } catch (err) {
    console.debug("[workoutSummary] readStart failed", err);
    return null;
  }
}

export function clearWorkoutStart(workoutId) {
  if (!workoutId) return;
  try {
    window.localStorage.removeItem(`${STORAGE_PREFIX}${workoutId}`);
  } catch (err) {
    console.debug("[workoutSummary] clearStart failed", err);
  }
}
